import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useAuth } from "../../../hooks/useAuth";
import { useToast } from "../../../hooks/useToast";
import { useUpdateStaffMutation } from "../../staff/staffApiSlice";
import { useUpdateUserMutation } from "../../user/userApiSlice";
import { getRoleLabel } from "../ui/settingsUtils";

const profileFormSchema = z.object({
  fullName: z.string().min(1, "Name is required"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  address: z.string().min(1, "Address is required"),
});

type ProfileFormData = z.infer<typeof profileFormSchema>;

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, isSuperAdmin, refetch } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [updateStaff, { isLoading: staffLoading, reset: resetStaff }] =
    useUpdateStaffMutation();
  const [updateUser, { isLoading: userLoading, reset: resetUser }] =
    useUpdateUserMutation();

  const isLoading = staffLoading || userLoading;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    mode: "onChange",
    defaultValues: {
      fullName: "",
      phoneNumber: "",
      address: "",
    },
  });

  useEffect(() => {
    if (isOpen && user) {
      reset({
        fullName: user.fullName || "",
        phoneNumber: user.phoneNumber || "",
        address: user.address || "",
      });
      setShowSuccess(false);
    }
  }, [isOpen, reset, user]);

  const handleClose = () => {
    setShowSuccess(false);
    reset();
    resetStaff();
    resetUser();
    onClose();
  };

  const onSubmit = async (data: ProfileFormData) => {
    if (isSubmitting || isLoading || !user?.id) return;
    if (!isDirty) {
      toast.warning({
        title: "No changes",
        message: "Update a field before saving.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isSuperAdmin) {
        await updateUser({
          id: user.id,
          fullName: data.fullName,
          email: user.email,
          phoneNumber: data.phoneNumber,
          address: data.address,
          role: getRoleLabel(user.roles, user.userType),
        }).unwrap();
      } else {
        await updateStaff({
          id: user.id,
          fullName: data.fullName,
          address: data.address,
          phoneNumber: data.phoneNumber,
        }).unwrap();
      }
      refetch();
      setShowSuccess(true);
      toast.success({
        title: "Profile updated",
        message: "Your details have been saved.",
      });
    } catch (err) {
      toast.error({
        title: "Could not update profile",
        message:
          getErrorDetails(err).message ||
          "Failed to update your profile. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit profile"
      className="max-w-[520px] w-full bg-white pb-8"
    >
      {showSuccess ? (
        <SuccessView
          handleCloseModal={handleClose}
          heading="Profile updated"
          message="Your profile details have been saved successfully."
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-6">
          <Controller
            control={control}
            name="fullName"
            render={({ field }) => (
              <AppInputField
                label="Full name"
                error={errors.fullName?.message}
                placeholder="Your name"
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <AppInputField
                label="Phone number"
                error={errors.phoneNumber?.message}
                placeholder="Phone number"
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="address"
            render={({ field }) => (
              <AppInputField
                label="Home address"
                error={errors.address?.message}
                placeholder="Home address"
                {...field}
              />
            )}
          />

          <div className="flex justify-end gap-3 pt-2">
            <AppButton
              type="button"
              appearance="soft"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting || isLoading}
              className="h-[42px] px-5"
            >
              Cancel
            </AppButton>
            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="h-[42px] px-5"
            >
              {isSubmitting || isLoading ? <Spinner /> : "Save changes"}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default EditProfileModal;
